import { useEffect, useRef, useState } from 'react';

type Options = {
  /** Fraction of the element that must be visible. */
  threshold?: number;
  /** Trigger only once and disconnect. */
  once?: boolean;
  /** Disable on small screens. */
  disableBelowPx?: number;
  /** rootMargin passed to IntersectionObserver. */
  rootMargin?: string;
};

/**
 * Lightweight, SSG-safe `useInView`.
 *
 * - On the server / initial paint we render `inView = false` so static HTML
 *   stays at the pre-animation state for elements that need it. Components
 *   that must remain visible without JS should not gate their content on this
 *   hook (they should toggle decorative classes only).
 * - On hydrate we mount an IntersectionObserver and flip to `true` when the
 *   ref enters the viewport.
 */
export const useInView = <T extends Element = HTMLDivElement>({
  threshold = 0.2,
  once = true,
  disableBelowPx,
  rootMargin = '0px 0px -10% 0px',
}: Options = {}) => {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof window === 'undefined') return;

    if (disableBelowPx && window.innerWidth < disableBelowPx) {
      setInView(true);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [threshold, once, disableBelowPx, rootMargin]);

  return { ref, inView };
};
